/**
 * PersonalityProfileEngine — adds temperament modifiers to Buddy's behavior.
 * A personality is a set of multipliers that subtly shift how Buddy reacts.
 */

export type PersonalityType = 'zen' | 'doomer' | 'maxi' | 'nodeMonk' | 'goblin';

export interface PersonalityProfile {
  type: PersonalityType;
  label: string;
  // Multipliers (1.0 = neutral)
  stressReactivity: number;      // how strongly Buddy reacts to stress
  priceReactivity: number;       // how strongly Buddy reacts to price moves
  blockReactivity: number;       // how strongly Buddy reacts to block events
  gestureFrequencyMod: number;   // multiplier on gesture frequency
  blinkSpeedMod: number;         // multiplier on blink interval
  breathingMod: number;          // multiplier on breathing depth
  expressionIntensity: number;   // how dramatic the facial expressions are
  calmBias: number;              // tendency toward calm (-1 to +1)
}

export const PERSONALITY_PROFILES: Record<PersonalityType, PersonalityProfile> = {
  zen: {
    type: 'zen',
    label: 'Zen Mode',
    stressReactivity: 0.6,
    priceReactivity: 0.5,
    blockReactivity: 0.8,
    gestureFrequencyMod: 0.7,
    blinkSpeedMod: 1.3,
    breathingMod: 1.2,
    expressionIntensity: 0.7,
    calmBias: 0.3,
  },
  doomer: {
    type: 'doomer',
    label: 'Doomer',
    stressReactivity: 1.5,
    priceReactivity: 1.3,
    blockReactivity: 1.1,
    gestureFrequencyMod: 1.3,
    blinkSpeedMod: 0.7,
    breathingMod: 0.8,
    expressionIntensity: 1.4,
    calmBias: -0.3,
  },
  maxi: {
    type: 'maxi',
    label: 'Maxi',
    stressReactivity: 0.8,
    priceReactivity: 1.6,
    blockReactivity: 1.3,
    gestureFrequencyMod: 1.1,
    blinkSpeedMod: 1.0,
    breathingMod: 1.1,
    expressionIntensity: 1.3,
    calmBias: 0.1,
  },
  nodeMonk: {
    type: 'nodeMonk',
    label: 'Node Monk',
    stressReactivity: 0.9,
    priceReactivity: 0.4,
    blockReactivity: 1.5,
    gestureFrequencyMod: 0.8,
    blinkSpeedMod: 1.2,
    breathingMod: 1.0,
    expressionIntensity: 0.8,
    calmBias: 0.15,
  },
  goblin: {
    type: 'goblin',
    label: 'Goblin Mode',
    stressReactivity: 1.2,
    priceReactivity: 1.4,
    blockReactivity: 1.2,
    gestureFrequencyMod: 1.5,
    blinkSpeedMod: 0.6,
    breathingMod: 0.9,
    expressionIntensity: 1.6,
    calmBias: -0.1,
  },
};

// Default personality
export const DEFAULT_PERSONALITY: PersonalityType = 'maxi';

/** Apply personality modifiers to baseline visual params */
export function applyPersonalityToBaseline(
  baseline: {
    blinkInterval: number;
    breathingDepth: number;
    gestureFrequency: number;
    eyeTension: number;
    smileBias: number;
    auraBrightness: number;
  },
  profile: PersonalityProfile,
) {
  return {
    blinkInterval: baseline.blinkInterval * profile.blinkSpeedMod,
    breathingDepth: baseline.breathingDepth * profile.breathingMod,
    gestureFrequency: baseline.gestureFrequency * profile.gestureFrequencyMod,
    eyeTension: baseline.eyeTension * profile.expressionIntensity,
    smileBias: baseline.smileBias + profile.calmBias * 0.15,
    auraBrightness: baseline.auraBrightness * (0.8 + profile.expressionIntensity * 0.25),
  };
}
